import React, { useState, useEffect } from "react";
import { AppConfig, DEFAULT_CONFIG, AuctionType } from "../types";
import { DropCatchService } from "../services/dropcatchService";
import { changePassword } from "../lib/auth-client";

interface UserConfig {
  id: string;
  user_id: string;
  dropcatch_client_id: string | null;
  dropcatch_client_secret: string | null;
  resend_api_key: string | null;
  google_api_key: string | null;
  email_notifications: boolean;
  notification_email: string | null;
}

interface Session {
  id: string;
  token: string;
  userId: string;
  expiresAt: Date;
  createdAt: Date;
  updatedAt: Date;
  ipAddress?: string;
  userAgent?: string;
}

interface LoginHistoryEntry {
  id: string;
  email: string;
  ip_address: string | null;
  user_agent: string | null;
  success: boolean;
  failure_reason: string | null;
  created_at: string;
}

interface SettingsProps {
  config: AppConfig;
  onSave: (config: AppConfig) => void;
  onClose: () => void;
  dropcatchService: DropCatchService;
  isAdmin?: boolean;
  userConfig?: UserConfig | null;
  onUpdateUserConfig?: (config: Partial<UserConfig>) => Promise<void>;
}

type TabId = "credentials" | "filters" | "notifications" | "account" | "security" | "sessions";

const API_BASE = import.meta.env.PROD
  ? 'https://zeropointdomains.com/api'
  : '/api';

const Settings: React.FC<SettingsProps> = ({
  config,
  onSave,
  onClose,
  isAdmin = false,
  userConfig,
  onUpdateUserConfig,
}) => {
  const [formData, setFormData] = useState<AppConfig>(config);
  const [activeTab, setActiveTab] = useState<TabId>("credentials");
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{ success: boolean; message: string } | null>(null);

  // Auth-related state
  const [dropcatchClientId, setDropcatchClientId] = useState(userConfig?.dropcatch_client_id || "");
  const [dropcatchClientSecret, setDropcatchClientSecret] = useState(userConfig?.dropcatch_client_secret || "");
  const [resendApiKey, setResendApiKey] = useState(userConfig?.resend_api_key || "");
  const [googleApiKey, setGoogleApiKey] = useState(userConfig?.google_api_key || "");
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaveMessage, setConfigSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Sessions state
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [revokingSession, setRevokingSession] = useState<string | null>(null);

  // Login history state
  const [loginHistory, setLoginHistory] = useState<LoginHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Update local state when userConfig changes
  useEffect(() => {
    if (userConfig) {
      setDropcatchClientId(userConfig.dropcatch_client_id || "");
      setDropcatchClientSecret(userConfig.dropcatch_client_secret || "");
      setResendApiKey(userConfig.resend_api_key || "");
      setGoogleApiKey(userConfig.google_api_key || "");
    }
  }, [userConfig]);

  // Load sessions when security tab is active
  useEffect(() => {
    if (activeTab === "sessions") {
      loadSessions();
    }
  }, [activeTab]);

  // Load login history when account tab is active
  useEffect(() => {
    if (activeTab === "account") {
      loadLoginHistory();
    }
  }, [activeTab]);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await fetch(`${API_BASE}/user/sessions`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadLoginHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_BASE}/user/login-history`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setLoginHistory(data.history || []);
      }
    } catch (error) {
      console.error("Failed to load login history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const revokeSession = async (sessionToken: string) => {
    setRevokingSession(sessionToken);
    try {
      const response = await fetch(`${API_BASE}/user/sessions`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sessionToken }),
      });
      if (response.ok) {
        setSessions((prev) => prev.filter((s) => s.token !== sessionToken));
      }
    } catch (error) {
      console.error("Failed to revoke session:", error);
    } finally {
      setRevokingSession(null);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "search_keywords" || name === "tlds" || name === "required_keywords") {
      setFormData((prev) => ({
        ...prev,
        [name]: value.split(",").map((s) => s.trim()).filter(Boolean),
      }));
    } else if (name === "auction_types") {
      const types = value.split(",").map((s) => s.trim()).filter(Boolean) as AuctionType[];
      setFormData((prev) => ({ ...prev, auction_types: types }));
    } else if (type === "number") {
      setFormData((prev) => ({ ...prev, [name]: value ? Number(value) : null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleSaveCredentials = async () => {
    if (!onUpdateUserConfig) return;

    setSavingConfig(true);
    setConfigSaveMessage(null);

    try {
      const updateData: Partial<UserConfig> = {
        dropcatch_client_id: dropcatchClientId || null,
        dropcatch_client_secret: dropcatchClientSecret || null,
      };

      if (isAdmin) {
        updateData.resend_api_key = resendApiKey || null;
        updateData.google_api_key = googleApiKey || null;
      }

      await onUpdateUserConfig(updateData);
      setConfigSaveMessage({ type: "success", text: "CREDENTIALS SAVED SUCCESSFULLY" });
    } catch (error) {
      setConfigSaveMessage({ type: "error", text: "FAILED TO SAVE CREDENTIALS" });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);

    const tempService = new DropCatchService({
      ...config,
      dropcatch_client_id: dropcatchClientId,
      dropcatch_client_secret: dropcatchClientSecret,
    });

    const result = await tempService.testConnection();
    setConnectionStatus(result);
    setTestingConnection(false);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError("PASSWORDS DO NOT MATCH");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("PASSWORD MUST BE AT LEAST 8 CHARACTERS");
      return;
    }

    setChangingPassword(true);
    try {
      const result = await changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: false,
      });

      if (result.error) {
        setPasswordError(result.error.message || "FAILED TO CHANGE PASSWORD");
      } else {
        setPasswordSuccess(true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error) {
      setPasswordError("FAILED TO CHANGE PASSWORD");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleReset = () => {
    setFormData(DEFAULT_CONFIG);
  };

  const formatDate = (date: string | Date) => {
    const d = new Date(date);
    return d.toLocaleString();
  };

  const parseUserAgent = (ua: string | null) => {
    if (!ua) return "Unknown Device";
    if (ua.includes("Chrome")) return "Chrome Browser";
    if (ua.includes("Firefox")) return "Firefox Browser";
    if (ua.includes("Safari")) return "Safari Browser";
    if (ua.includes("Edge")) return "Edge Browser";
    return "Unknown Browser";
  };

  const tabs: { id: TabId; label: string; shortcut: string }[] = [
    { id: "credentials", label: "API KEYS", shortcut: "1" },
    { id: "filters", label: "FILTERS", shortcut: "2" },
    { id: "notifications", label: "ALERTS", shortcut: "3" },
    { id: "account", label: "ACCOUNT", shortcut: "4" },
    { id: "security", label: "SECURITY", shortcut: "5" },
    { id: "sessions", label: "SESSIONS", shortcut: "6" },
  ];

  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 font-mono"
      style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.5)" }}
    >
      <div
        className="bg-black border-2 border-green-500 w-full max-w-4xl max-h-[90vh] flex flex-col"
        style={{ boxShadow: "0 0 30px rgba(34, 197, 94, 0.3)" }}
      >
        {/* Header */}
        <div className="p-4 border-b-2 border-green-500 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-green-400 tracking-wider">{">"} CONFIG.EXE</h2>
            <p className="text-green-600 text-xs mt-1">SYSTEM CONFIGURATION UTILITY v3.0</p>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1 border border-green-500 text-green-500 hover:bg-green-500 hover:text-black transition-colors text-sm"
          >
            [ESC] CLOSE
          </button>
        </div>

        {/* Tabs */}
        <div className="px-4 pt-3 border-b border-green-900 overflow-x-auto">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 text-xs tracking-wider transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-green-500 text-black border-t-2 border-l-2 border-r-2 border-green-500"
                    : "text-green-600 hover:text-green-400 border border-transparent hover:border-green-700"
                }`}
              >
                [{tab.shortcut}] {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Credentials Tab */}
          {activeTab === "credentials" && (
            <div className="space-y-6">
              {/* DropCatch Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b border-green-900 pb-2">
                  <span className="text-green-400">{">>>"}</span>
                  <h3 className="text-green-400 font-bold tracking-wider">DROPCATCH API</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-green-600 mb-1 tracking-wider">CLIENT_ID:</label>
                    <input
                      type="text"
                      value={dropcatchClientId}
                      onChange={(e) => setDropcatchClientId(e.target.value)}
                      className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                      placeholder="ENTER CLIENT ID..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-green-600 mb-1 tracking-wider">CLIENT_SECRET:</label>
                    <input
                      type="password"
                      value={dropcatchClientSecret}
                      onChange={(e) => setDropcatchClientSecret(e.target.value)}
                      className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                      placeholder="********"
                    />
                  </div>
                </div>

                {/* Connection Test */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleTestConnection}
                    disabled={testingConnection || !dropcatchClientId || !dropcatchClientSecret}
                    className="px-4 py-2 border border-green-700 text-green-500 hover:border-green-500 hover:bg-green-950 transition-colors disabled:opacity-50 text-sm tracking-wider"
                  >
                    {testingConnection ? (
                      <span className="animate-pulse">[TESTING...]</span>
                    ) : (
                      "[TEST CONNECTION]"
                    )}
                  </button>

                  {connectionStatus && (
                    <span className={`text-sm ${connectionStatus.success ? "text-green-400" : "text-green-300 animate-pulse"}`}>
                      {connectionStatus.success ? "CONNECTION OK" : `ERROR: ${connectionStatus.message}`}
                    </span>
                  )}
                </div>
              </div>

              {/* Admin-only: Google AI Section */}
              {isAdmin && (
                <div className="space-y-4 pt-4 border-t border-green-900">
                  <div className="flex items-center gap-2 border-b border-green-900 pb-2">
                    <span className="text-yellow-400">{">>>"}</span>
                    <h3 className="text-yellow-400 font-bold tracking-wider">GOOGLE GEMINI AI [ADMIN]</h3>
                  </div>

                  <div>
                    <label className="block text-xs text-green-600 mb-1 tracking-wider">API_KEY:</label>
                    <input
                      type="password"
                      value={googleApiKey}
                      onChange={(e) => setGoogleApiKey(e.target.value)}
                      className="w-full bg-black border-2 border-yellow-700 focus:border-yellow-500 px-3 py-2 text-green-400 outline-none"
                      placeholder="AIza..."
                    />
                    <p className="text-xs text-green-700 mt-1">
                      OBTAIN KEY: aistudio.google.com/apikey
                    </p>
                  </div>
                </div>
              )}

              {/* Admin-only: Resend Email Section */}
              {isAdmin && (
                <div className="space-y-4 pt-4 border-t border-green-900">
                  <div className="flex items-center gap-2 border-b border-green-900 pb-2">
                    <span className="text-yellow-400">{">>>"}</span>
                    <h3 className="text-yellow-400 font-bold tracking-wider">RESEND EMAIL [ADMIN]</h3>
                  </div>

                  <div>
                    <label className="block text-xs text-green-600 mb-1 tracking-wider">API_KEY:</label>
                    <input
                      type="password"
                      value={resendApiKey}
                      onChange={(e) => setResendApiKey(e.target.value)}
                      className="w-full bg-black border-2 border-yellow-700 focus:border-yellow-500 px-3 py-2 text-green-400 outline-none"
                      placeholder="re_..."
                    />
                    <p className="text-xs text-green-700 mt-1">
                      OBTAIN KEY: resend.com/api-keys
                    </p>
                  </div>
                </div>
              )}

              {/* Save Credentials Button */}
              {onUpdateUserConfig && (
                <div className="pt-4 border-t border-green-900">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleSaveCredentials}
                      disabled={savingConfig}
                      className="px-6 py-2 bg-green-500 text-black hover:bg-green-400 transition-colors text-sm tracking-wider font-bold disabled:opacity-50"
                    >
                      {savingConfig ? "[SAVING...]" : "[SAVE CREDENTIALS]"}
                    </button>
                    {configSaveMessage && (
                      <span className={`text-sm ${configSaveMessage.type === "success" ? "text-green-400" : "text-red-400"}`}>
                        {configSaveMessage.text}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Filters Tab */}
          {activeTab === "filters" && (
            <div className="space-y-6">
              {/* Keywords */}
              <div>
                <label className="block text-xs text-green-600 mb-1 tracking-wider">
                  SEARCH_KEYWORDS (COMMA SEPARATED):
                </label>
                <textarea
                  name="search_keywords"
                  value={formData.search_keywords.join(", ")}
                  onChange={handleChange}
                  className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none h-24 resize-none"
                  placeholder="law, legal, attorney, lawyer, court..."
                />
              </div>

              <div>
                <label className="block text-xs text-green-600 mb-1 tracking-wider">
                  REQUIRED_KEYWORDS (COMMA SEPARATED):
                </label>
                <input
                  type="text"
                  name="required_keywords"
                  value={formData.required_keywords.join(", ")}
                  onChange={handleChange}
                  className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                  placeholder="MUST CONTAIN THESE KEYWORDS..."
                />
              </div>

              {/* TLDs & Types */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">TLD_FILTER:</label>
                  <input
                    type="text"
                    name="tlds"
                    value={formData.tlds.join(", ")}
                    onChange={handleChange}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="com, net, org, io, ai"
                  />
                </div>
                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">AUCTION_TYPES:</label>
                  <input
                    type="text"
                    name="auction_types"
                    value={formData.auction_types.join(", ")}
                    onChange={handleChange}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="Dropped, PrivateSeller, PreRelease"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="flex flex-wrap gap-6 py-2">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 border-2 ${formData.no_dashes ? "bg-green-500 border-green-500" : "border-green-600"} flex items-center justify-center`}>
                    {formData.no_dashes && <span className="text-black text-xs">X</span>}
                  </div>
                  <input
                    type="checkbox"
                    name="no_dashes"
                    checked={formData.no_dashes}
                    onChange={handleChange}
                    className="hidden"
                  />
                  <span className="text-sm text-green-500 group-hover:text-green-400">NO_DASHES</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 border-2 ${formData.no_numbers ? "bg-green-500 border-green-500" : "border-green-600"} flex items-center justify-center`}>
                    {formData.no_numbers && <span className="text-black text-xs">X</span>}
                  </div>
                  <input
                    type="checkbox"
                    name="no_numbers"
                    checked={formData.no_numbers}
                    onChange={handleChange}
                    className="hidden"
                  />
                  <span className="text-sm text-green-500 group-hover:text-green-400">NO_NUMBERS</span>
                </label>
              </div>

              {/* Price Range */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">MIN_PRICE:</label>
                  <input
                    type="number"
                    name="min_price"
                    value={formData.min_price || ""}
                    onChange={handleChange}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">MAX_PRICE:</label>
                  <input
                    type="number"
                    name="max_price"
                    value={formData.max_price || ""}
                    onChange={handleChange}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="10000"
                  />
                </div>
                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">MIN_LENGTH:</label>
                  <input
                    type="number"
                    name="min_length"
                    value={formData.min_length || ""}
                    onChange={handleChange}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="3"
                  />
                </div>
                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">MAX_LENGTH:</label>
                  <input
                    type="number"
                    name="max_length"
                    value={formData.max_length || ""}
                    onChange={handleChange}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="20"
                  />
                </div>
              </div>

              {/* Has Bids Filter */}
              <div>
                <label className="block text-xs text-green-600 mb-2 tracking-wider">BID_STATUS_FILTER:</label>
                <div className="flex gap-4">
                  {[
                    { value: null, label: "ALL" },
                    { value: true, label: "WITH_BIDS" },
                    { value: false, label: "NO_BIDS" },
                  ].map((option) => (
                    <label key={String(option.value)} className="flex items-center gap-2 cursor-pointer group">
                      <div className={`w-4 h-4 border-2 rounded-full ${formData.has_bids === option.value ? "bg-green-500 border-green-500" : "border-green-600"}`} />
                      <input
                        type="radio"
                        name="has_bids"
                        checked={formData.has_bids === option.value}
                        onChange={() => setFormData((prev) => ({ ...prev, has_bids: option.value }))}
                        className="hidden"
                      />
                      <span className="text-sm text-green-500 group-hover:text-green-400">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="space-y-6">
              <div className="border border-green-700 bg-green-950/30 p-4">
                <div className="flex items-start gap-3">
                  <span className="text-green-400">[!]</span>
                  <div>
                    <p className="text-green-400 font-bold tracking-wider">SYSTEM NOTICE</p>
                    <p className="text-green-600 text-sm mt-1">
                      EMAIL NOTIFICATION MODULE REQUIRES BACKEND SERVICE.
                      FEATURE COMING IN NEXT UPDATE.
                    </p>
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer opacity-50">
                <div className={`w-4 h-4 border-2 ${formData.email_notifications ? "bg-green-500 border-green-500" : "border-green-600"} flex items-center justify-center`}>
                  {formData.email_notifications && <span className="text-black text-xs">X</span>}
                </div>
                <input
                  type="checkbox"
                  name="email_notifications"
                  checked={formData.email_notifications}
                  onChange={handleChange}
                  className="hidden"
                  disabled
                />
                <div>
                  <span className="text-green-500 font-medium tracking-wider">ENABLE_EMAIL_ALERTS</span>
                  <p className="text-green-700 text-xs">NOTIFY ON NEW MATCHING DOMAINS</p>
                </div>
              </label>

              <div className="opacity-50">
                <label className="block text-xs text-green-600 mb-1 tracking-wider">NOTIFICATION_EMAIL:</label>
                <input
                  type="email"
                  name="notification_email"
                  value={formData.notification_email}
                  onChange={handleChange}
                  className="w-full bg-black border-2 border-green-700 px-3 py-2 text-green-400 outline-none"
                  placeholder="user@domain.com"
                  disabled
                />
              </div>
            </div>
          )}

          {/* Account Tab - Login History */}
          {activeTab === "account" && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 border-b border-green-900 pb-2">
                <span className="text-green-400">{">>>"}</span>
                <h3 className="text-green-400 font-bold tracking-wider">LOGIN HISTORY</h3>
              </div>

              {loadingHistory ? (
                <div className="text-center py-8">
                  <span className="text-green-500 animate-pulse">[LOADING...]</span>
                </div>
              ) : loginHistory.length === 0 ? (
                <div className="text-center py-8 text-green-600">
                  <p>NO LOGIN HISTORY FOUND</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {loginHistory.map((entry) => (
                    <div
                      key={entry.id}
                      className={`p-3 border ${entry.success ? "border-green-700" : "border-red-700"}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 ${entry.success ? "bg-green-500 text-black" : "bg-red-500 text-white"}`}>
                              {entry.success ? "SUCCESS" : "FAILED"}
                            </span>
                            <span className="text-green-400 text-sm">{entry.email}</span>
                          </div>
                          <p className="text-green-600 text-xs mt-1">
                            IP: {entry.ip_address || "Unknown"}
                          </p>
                          <p className="text-green-700 text-xs">
                            {parseUserAgent(entry.user_agent)}
                          </p>
                          {entry.failure_reason && (
                            <p className="text-red-400 text-xs mt-1">
                              REASON: {entry.failure_reason}
                            </p>
                          )}
                        </div>
                        <span className="text-green-600 text-xs">
                          {formatDate(entry.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Security Tab - Password Change */}
          {activeTab === "security" && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 border-b border-green-900 pb-2">
                <span className="text-green-400">{">>>"}</span>
                <h3 className="text-green-400 font-bold tracking-wider">CHANGE PASSWORD</h3>
              </div>

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">CURRENT_PASSWORD:</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="********"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">NEW_PASSWORD:</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="MIN 8 CHARACTERS"
                    required
                    minLength={8}
                  />
                </div>

                <div>
                  <label className="block text-xs text-green-600 mb-1 tracking-wider">CONFIRM_PASSWORD:</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                    placeholder="********"
                    required
                  />
                </div>

                {passwordError && (
                  <div className="text-red-400 text-sm">{passwordError}</div>
                )}

                {passwordSuccess && (
                  <div className="text-green-400 text-sm">PASSWORD CHANGED SUCCESSFULLY</div>
                )}

                <button
                  type="submit"
                  disabled={changingPassword}
                  className="px-6 py-2 bg-green-500 text-black hover:bg-green-400 transition-colors text-sm tracking-wider font-bold disabled:opacity-50"
                >
                  {changingPassword ? "[UPDATING...]" : "[UPDATE PASSWORD]"}
                </button>
              </form>
            </div>
          )}

          {/* Sessions Tab */}
          {activeTab === "sessions" && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 border-b border-green-900 pb-2">
                <span className="text-green-400">{">>>"}</span>
                <h3 className="text-green-400 font-bold tracking-wider">ACTIVE SESSIONS</h3>
              </div>

              {loadingSessions ? (
                <div className="text-center py-8">
                  <span className="text-green-500 animate-pulse">[LOADING...]</span>
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-green-600">
                  <p>NO ACTIVE SESSIONS FOUND</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <div key={session.id} className="p-3 border border-green-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-green-400 text-sm font-bold">
                            {parseUserAgent(session.userAgent || null)}
                          </p>
                          <p className="text-green-600 text-xs mt-1">
                            IP: {session.ipAddress || "Unknown"}
                          </p>
                          <p className="text-green-700 text-xs">
                            Created: {formatDate(session.createdAt)}
                          </p>
                          <p className="text-green-700 text-xs">
                            Expires: {formatDate(session.expiresAt)}
                          </p>
                        </div>
                        <button
                          onClick={() => revokeSession(session.token)}
                          disabled={revokingSession === session.token}
                          className="px-3 py-1 border border-red-500 text-red-500 hover:bg-red-500 hover:text-black transition-colors text-xs tracking-wider disabled:opacity-50"
                        >
                          {revokingSession === session.token ? "[...]" : "[REVOKE]"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="border border-yellow-500/50 bg-yellow-950/30 p-3 mt-4">
                <p className="text-yellow-500 text-xs">
                  [WARNING] REVOKING THE CURRENT SESSION WILL LOG YOU OUT
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 border-green-500 flex justify-between items-center bg-black">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-green-600 hover:text-green-400 text-sm tracking-wider"
          >
            [RESET DEFAULTS]
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-green-700 text-green-500 hover:border-green-500 transition-colors text-sm tracking-wider"
            >
              [CANCEL]
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-500 text-black hover:bg-green-400 transition-colors text-sm tracking-wider font-bold"
              style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.5)" }}
            >
              [SAVE CONFIG]
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
